import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import type { Identifier, XYCoord } from 'dnd-core';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    IconButton,
    Switch,
    FormControlLabel,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Grid
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    DragIndicator as DragIndicatorIcon,
    Delete as DeleteIcon,
    Image as ImageIcon,
    VideoLibrary as VideoIcon,
    Code as CodeIcon
} from '@mui/icons-material';
import { HtmlEditor } from '../../core/HtmlEditor/HtmlEditor';
import { WelcomeBoardContentType, WelcomeBoardService } from '../../../common/interfaces';
import { FileSelector } from '../FileSelector';
import type { FileType } from '../FileManager';

interface DraggableServiceItemProps {
    service: WelcomeBoardService;
    index: number;
    expanded: boolean;
    onExpand: (index: number) => void;
    onUpdate: (index: number, service: WelcomeBoardService) => void;
    onDelete: (index: number) => void;
    onMove: (dragIndex: number, hoverIndex: number) => void;
    onOpenFileManager: (target: FileType, index: number) => void;
    clientId: string;
}

interface DragItem {
    index: number;
    id: string;
    type: string;
}

const ITEM_TYPE = 'SERVICE_ITEM';

export const DraggableServiceItem: React.FC<DraggableServiceItemProps> = ({
    service,
    index,
    expanded,
    onExpand,
    onUpdate,
    onDelete,
    onMove,
    onOpenFileManager,
    clientId
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const previewRef = useRef<HTMLDivElement>(null);

    const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
        accept: ITEM_TYPE,
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item: DragItem, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }

            // Determine rectangle on screen
            const hoverBoundingRect = ref.current?.getBoundingClientRect();

            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

            // Determine mouse position
            const clientOffset = monitor.getClientOffset();

            // Get pixels to the top
            const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            // Time to actually perform the action
            onMove(dragIndex, hoverIndex);

            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type: ITEM_TYPE,
        item: () => {
            return { id: `service-${index}`, index };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const dragHandleRef = useRef<HTMLDivElement>(null);
    drag(dragHandleRef);
    preview(previewRef);
    drop(ref);

    const getContentTypeIcon = () => {
        const contentType = service.selectedContentType || 'html';
        switch (contentType) {
            case 'image':
                return <ImageIcon fontSize="small" />;
            case 'video':
                return <VideoIcon fontSize="small" />;
            case 'html':
            default:
                return <CodeIcon fontSize="small" />;
        }
    };

    const getContentTypeColor = (): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
        const contentType = service.selectedContentType || 'html';
        switch (contentType) {
            case 'image':
                return 'success';
            case 'video':
                return 'secondary';
            case 'html':
            default:
                return 'primary';
        }
    };

    const getPreviewContent = () => {
        const contentType = service.selectedContentType || 'html';

        if (contentType === 'html' && service.htmlContent) {
            // Extract text content or show snippet
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = service.htmlContent;
            const textContent = tempDiv.textContent || tempDiv.innerText || '';
            return textContent.substring(0, 100) + (textContent.length > 100 ? '...' : '');
        } else if (contentType === 'image' && service.imageFile) {
            return service.imageFile.split('/').pop() || 'Image file';
        } else if (contentType === 'video' && service.videoFile) {
            return service.videoFile.split('/').pop() || 'Video file';
        }

        return 'No content';
    };

    const handleTitleChange = (title: string) => {
        onUpdate(index, { ...service, title });
    };

    const handleEnabledChange = (isEnabled: boolean) => {
        onUpdate(index, { ...service, isEnabled });
    };

    const handleContentTypeChange = (contentType: WelcomeBoardContentType) => {
        onUpdate(index, { ...service, selectedContentType: contentType });
    };

    const handleHtmlContentChange = (htmlContent: string) => {
        onUpdate(index, { ...service, htmlContent });
    };

    const handleImageFileChange = (imageFile: string) => {
        onUpdate(index, { ...service, imageFile });
    };

    const handleVideoFileChange = (videoFile: string) => {
        onUpdate(index, { ...service, videoFile });
    };

    const opacity = isDragging ? 0.4 : 1;

    return (
        <Box
            ref={ref}
            data-handler-id={handlerId}
            sx={{
                opacity,
                mb: 1,
                transition: 'opacity 0.2s'
            }}
        >
            <Accordion
                expanded={expanded}
                onChange={() => onExpand(index)}
                sx={{
                    '&:before': {
                        display: 'none'
                    },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '4px !important',
                    '&.Mui-expanded': {
                        margin: 0,
                        marginBottom: 1
                    }
                }}
            >
                <AccordionSummary
                    ref={previewRef}
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        '&.Mui-expanded': {
                            minHeight: 48
                        },
                        '& .MuiAccordionSummary-content': {
                            alignItems: 'center',
                            '&.Mui-expanded': {
                                margin: '12px 0'
                            }
                        }
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            gap: 2
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Box
                            ref={dragHandleRef}
                            sx={{
                                cursor: 'grab',
                                display: 'flex',
                                alignItems: 'center',
                                color: 'text.secondary',
                                '&:active': {
                                    cursor: 'grabbing'
                                }
                            }}
                        >
                            <DragIndicatorIcon />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {service.title || `Service ${index + 1}`}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block'
                                }}
                            >
                                {getPreviewContent()}
                            </Typography>
                        </Box>

                        <Chip
                            icon={getContentTypeIcon()}
                            label={service.selectedContentType || 'HTML'}
                            size="small"
                            color={getContentTypeColor()}
                            sx={{ textTransform: 'uppercase' }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={service.isEnabled}
                                    onChange={(e) => handleEnabledChange(e.target.checked)}
                                    size="small"
                                />
                            }
                            label="Enabled"
                            sx={{ margin: 0 }}
                        />

                        <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(index);
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                </AccordionSummary>

                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Service Title"
                                value={service.title || ''}
                                onChange={(e) => handleTitleChange(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Content Type</InputLabel>
                                <Select
                                    value={service.selectedContentType || 'html'}
                                    label="Content Type"
                                    onChange={(e) => handleContentTypeChange(e.target.value as WelcomeBoardContentType)}
                                >
                                    <MenuItem value="html">HTML</MenuItem>
                                    <MenuItem value="image">Image</MenuItem>
                                    <MenuItem value="video">Video</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            {(service.selectedContentType === 'html' || service.selectedContentType == null) && (
                                <HtmlEditor
                                    value={service.htmlContent || ''}
                                    onChange={handleHtmlContentChange}
                                    placeholder="Enter service HTML"
                                    height={400}
                                />
                            )}
                            {service.selectedContentType === 'image' && (
                                <FileSelector
                                    filePath={service.imageFile}
                                    onFileChange={(filePath) => handleImageFileChange(filePath || '')}
                                    onBrowseClick={() => onOpenFileManager('image', index)}
                                    clientId={clientId}
                                    label="Image File"
                                    fileType="image"
                                />
                            )}
                            {service.selectedContentType === 'video' && (
                                <FileSelector
                                    filePath={service.videoFile}
                                    onFileChange={(filePath) => handleVideoFileChange(filePath || '')}
                                    onBrowseClick={() => onOpenFileManager('video', index)}
                                    clientId={clientId}
                                    label="Video File"
                                    fileType="video"
                                />
                            )}
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};
